import { useEffect } from 'react';
import { Card, Form, Input, Button, Typography, App as AntApp } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useLoginMutation, isContextRequired } from './hooks';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuthStore } from '@starling/core/auth/store';
import type { ContextPickerState } from './ContextPickerPage';
import { ApiError } from '@starling/core/api/errors';

const { Title, Text } = Typography;

interface LoginValues {
  email: string;
  password: string;
}

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = AntApp.useApp();
  const mutation = useLoginMutation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  const from = (location.state as LocationState | null)?.from;

  // Целевой маршрут после входа зависит от роли: platform_admin ведём в раздел
  // платформы `/platform/*` (у него нет тенантских данных), тенантные роли —
  // в клиентскую админку. `from` (путь возврата после гарда) учитываем только
  // если он совместим с ролью.
  const resolveTarget = (): string => {
    const role = useAuthStore.getState().user?.role;
    if (role === 'platform_admin') {
      return from && from.startsWith('/platform') ? from : '/platform';
    }
    return from && !from.startsWith('/platform') ? from : '/customers';
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(resolveTarget(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, from, navigate]);

  const onFinish = (values: LoginValues): void => {
    mutation.mutate(values, {
      onSuccess: (data) => {
        // Мультиконтекст: у личности 2+ активных membership. Токены НЕ выданы —
        // ведём на экран «Войти как…». select_token и список контекстов передаём
        // через location.state (в памяти, не в localStorage — токен короткоживущий).
        if (isContextRequired(data)) {
          navigate('/select-context', {
            state: {
              select_token: data.select_token,
              contexts: data.contexts,
              email: values.email,
            } satisfies ContextPickerState,
          });
          return;
        }
        navigate(resolveTarget(), { replace: true });
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 401) {
          void message.error(t('auth.login.errors.invalid_credentials'));
        } else {
          void message.error(err instanceof Error ? err.message : t('auth.login.errors.generic'));
        }
      },
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--mist)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 420 }} variant="borderless">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('auth.login.title')}
          </Title>
          <Text type="secondary">{t('auth.login.subtitle')}</Text>
        </div>
        <Form<LoginValues>
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          disabled={mutation.isPending}
        >
          <Form.Item
            label={t('auth.login.email')}
            name="email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item
            label={t('auth.login.password')}
            name="password"
            rules={[{ required: true, min: 1 }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <div style={{ textAlign: 'right', marginTop: -12, marginBottom: 16 }}>
            <Link to="/forgot-password">{t('auth.login.forgot_password')}</Link>
          </div>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={mutation.isPending}
              size="large"
            >
              {t('auth.login.submit')}
            </Button>
          </Form.Item>
        </Form>
        {/* Google-вход (M7.5): рендерится только при заданном VITE_GOOGLE_CLIENT_ID. */}
        <GoogleSignInButton />
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Text type="secondary">{t('auth.login.no_account')} </Text>
          <Link to="/register">{t('auth.login.register_link')}</Link>
        </div>
      </Card>
    </div>
  );
}
